import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Mail, Phone, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/enquiries`);
            setRows(data);
        } catch (e) {
            toast.error("Failed to load enquiries");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const updateStatus = async (id, status) => {
        try {
            await axios.patch(`${API}/enquiries/${id}?status=${status}`);
            setRows((r) => r.map((x) => (x.id === id ? { ...x, status } : x)));
            toast.success(`Marked ${status}`);
        } catch {
            toast.error("Could not update status");
        }
    };

    const filtered = rows.filter((r) => {
        if (filter !== "all" && r.status !== filter) return false;
        if (!search) return true;
        const q = search.toLowerCase();
        return (
            r.name.toLowerCase().includes(q) ||
            r.email.toLowerCase().includes(q) ||
            (r.company || "").toLowerCase().includes(q) ||
            (r.coal_type || "").toLowerCase().includes(q)
        );
    });

    const counts = {
        all: rows.length,
        new: rows.filter((r) => r.status === "new").length,
        contacted: rows.filter((r) => r.status === "contacted").length,
        closed: rows.filter((r) => r.status === "closed").length,
    };

    return (
        <div data-testid="page-admin" className="pt-32 pb-24 bg-coal-900 min-h-screen">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
                    <div>
                        <div className="text-xs tracking-[0.25em] uppercase text-ember font-bold mb-3">
                            Internal · Trade Desk
                        </div>
                        <h1 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight text-white">
                            Enquiry Inbox
                        </h1>
                    </div>
                    <button
                        onClick={load}
                        data-testid="admin-refresh"
                        className="bg-coal-800 border border-white/15 hover:border-white text-white px-6 py-3 text-xs tracking-[0.2em] uppercase font-bold flex items-center gap-2 self-start md:self-auto"
                    >
                        <RefreshCw className="w-4 h-4" /> Refresh
                    </button>
                </div>

                {/* Filters */}
                <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
                    <div className="flex gap-px bg-white/10 border border-white/10">
                        {["all", "new", "contacted", "closed"].map((s) => (
                            <button
                                key={s}
                                onClick={() => setFilter(s)}
                                data-testid={`admin-filter-${s}`}
                                className={`px-4 py-2 text-xs uppercase tracking-widest font-bold transition-colors ${
                                    filter === s
                                        ? "bg-ember text-white"
                                        : "bg-coal-800 text-zinc-400 hover:text-white"
                                }`}
                            >
                                {s} ({counts[s]})
                            </button>
                        ))}
                    </div>
                    <input
                        type="text"
                        placeholder="Search name, email, company..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        data-testid="admin-search"
                        className="flex-1 bg-coal-800 border border-white/10 focus:border-ember focus:outline-none text-white px-4 py-2 text-sm"
                    />
                </div>

                {/* Table */}
                <div className="border border-white/10 overflow-x-auto">
                    {loading ? (
                        <div className="p-16 text-center text-zinc-500 flex items-center justify-center gap-3">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Loading…
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="p-16 text-center text-zinc-500">
                            No enquiries{filter !== "all" ? ` in '${filter}'` : ""} yet.
                        </div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-coal-800 text-zinc-500 uppercase tracking-wider text-[10px]">
                                    <th className="text-left p-4">Date</th>
                                    <th className="text-left p-4">Contact</th>
                                    <th className="text-left p-4">Company</th>
                                    <th className="text-left p-4">Spec</th>
                                    <th className="text-left p-4">Message</th>
                                    <th className="text-left p-4">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((r, i) => (
                                    <tr
                                        key={r.id}
                                        data-testid={`admin-row-${r.id}`}
                                        className={`${
                                            i % 2 === 0
                                                ? "bg-coal-900"
                                                : "bg-coal-800"
                                        } text-zinc-200 align-top border-t border-white/5`}
                                    >
                                        <td className="p-4 font-mono text-xs text-zinc-400 whitespace-nowrap">
                                            {new Date(r.created_at).toLocaleString()}
                                        </td>
                                        <td className="p-4">
                                            <div className="text-white font-medium">
                                                {r.name}
                                            </div>
                                            <a
                                                href={`mailto:${r.email}`}
                                                className="text-xs text-ember hover:underline flex items-center gap-1 mt-1"
                                            >
                                                <Mail className="w-3 h-3" />
                                                {r.email}
                                            </a>
                                            <a
                                                href={`tel:${r.phone}`}
                                                className="text-xs text-zinc-400 hover:text-white flex items-center gap-1 mt-1"
                                            >
                                                <Phone className="w-3 h-3" />
                                                {r.phone}
                                            </a>
                                        </td>
                                        <td className="p-4 text-zinc-300">
                                            {r.company || "—"}
                                        </td>
                                        <td className="p-4 text-zinc-300 text-xs font-mono">
                                            <div>{r.coal_type || "—"}</div>
                                            <div className="text-zinc-500">
                                                {r.quantity || ""}
                                            </div>
                                            <div className="text-zinc-500">
                                                {r.destination_port || ""}
                                            </div>
                                        </td>
                                        <td className="p-4 text-zinc-400 max-w-md">
                                            <p className="line-clamp-3">
                                                {r.message}
                                            </p>
                                        </td>
                                        <td className="p-4">
                                            <select
                                                value={r.status}
                                                onChange={(e) =>
                                                    updateStatus(r.id, e.target.value)
                                                }
                                                data-testid={`admin-status-${r.id}`}
                                                className={`bg-coal-700 border border-white/10 text-xs px-2 py-1 uppercase tracking-wider ${
                                                    r.status === "new"
                                                        ? "text-ember"
                                                        : r.status === "contacted"
                                                          ? "text-amber-400"
                                                          : "text-emerald-400"
                                                }`}
                                            >
                                                <option value="new">New</option>
                                                <option value="contacted">
                                                    Contacted
                                                </option>
                                                <option value="closed">
                                                    Closed
                                                </option>
                                            </select>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className="mt-6 text-xs text-zinc-600 font-mono">
                    Total: {rows.length} · Showing: {filtered.length}
                </div>
            </div>
        </div>
    );
}
